import { Link } from "react-router-dom";
import SectionHeading from "@/components/base/SectionHeading";
import { usePageMeta } from "@/hooks/usePageMeta";

const stats = [
  { value: "3+", label: "覆盖体育项目", icon: "ri-apps-2-line" },
  { value: "500+", label: "每日更新赛事", icon: "ri-refresh-line" },
  { value: "24/7", label: "全天候服务", icon: "ri-time-line" },
  { value: "百万级", label: "累计观众", icon: "ri-group-line" },
];

const values = [
  {
    icon: "ri-flashlight-line",
    title: "实时优先",
    desc: "以赛事实时动态为核心，比分、进度与状态始终保持同步，让用户第一时间掌握赛场变化。",
  },
  {
    icon: "ri-layout-grid-line",
    title: "一站式聚合",
    desc: "篮球、足球、电竞多项目集中呈现，用户无需在多个平台间来回切换，即可纵览全部赛事。",
  },
  {
    icon: "ri-shield-check-line",
    title: "稳定可靠",
    desc: "持续打磨观看与浏览体验，让每一次打开都能快速找到想看的那场比赛。",
  },
];

const team = [
  { name: "赛事内容组", role: "赛程与数据整理", icon: "ri-list-check-2" },
  { name: "产品体验组", role: "观看与浏览体验设计", icon: "ri-layout-4-line" },
  { name: "技术支持组", role: "播放与稳定性保障", icon: "ri-cpu-line" },
];

export default function AboutPage() {
  usePageMeta(
    "关于我们 - jrs直播",
    "jrs直播是专注于篮球、足球、电竞等体育赛事直播的平台，为体育爱好者提供多赛事并行观看、实时比分与赛程浏览的一站式体验。了解我们的定位与团队。",
    "关于jrs直播,体育直播平台,赛事直播,jrs直播团队,体育赛事门户",
  );

  return (
    <>
      <section className="border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
          <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-400">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">关于我们</span>
          </nav>
          <h1 className="mt-5 font-heading text-3xl font-extrabold tracking-tight text-foreground-950 md:text-4xl">
            关于 jrs直播
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            jrs直播是一个聚焦体育赛事直播的内容平台，面向热爱篮球、足球、电竞的广大体育迷与赛事观众。
            我们相信，看比赛不该是一件麻烦事——聚合多项目、同步实时动态、支持多赛事并行浏览，
            让每一位用户都能轻松找到并享受自己热爱的比赛。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {stats.map((s) => (
            <div
              key={s.label}
              className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500/15 text-primary-400">
                <i className={`${s.icon} text-xl`} />
              </span>
              <div className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
                {s.value}
              </div>
              <div className="mt-1 text-xs text-foreground-500 md:text-sm">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
          <SectionHeading
            eyebrow="Our Values"
            title="我们坚持的三件事"
            description="从内容到体验，我们希望把每一场比赛都呈现得足够清楚、足够顺畅。"
          />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {values.map((v) => (
              <div
                key={v.title}
                className="rounded-lg border border-background-200 bg-background-50 p-6"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-500/15 text-accent-400">
                  <i className={`${v.icon} text-xl`} />
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  {v.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  {v.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="Our Team"
          title="背后的团队"
          description="我们是一支热爱体育的小团队，各自负责内容、体验与技术的不同环节。"
        />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {team.map((member) => (
            <div
              key={member.name}
              className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-100 p-5"
            >
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-500/15 text-primary-400">
                <i className={`${member.icon} text-2xl`} />
              </span>
              <div>
                <h3 className="font-heading text-base font-bold text-foreground-950">
                  {member.name}
                </h3>
                <p className="mt-0.5 text-sm text-foreground-600">
                  {member.role}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-start gap-4 rounded-lg border border-primary-500/30 bg-primary-500/10 p-6 md:flex-row md:items-center md:justify-between md:p-8">
          <div>
            <h3 className="font-heading text-xl font-bold text-foreground-950">
              想和我们聊聊？
            </h3>
            <p className="mt-1 text-sm text-foreground-600">
              无论是合作、建议还是内容反馈，我们都非常欢迎。
            </p>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-mail-send-line" />
            联系我们
          </Link>
        </div>
      </section>
    </>
  );
}